import { Thunk } from '../../../../../types/reduxTypes';
import Immutable from 'immutable';

// Actions
import { createLabel, getNewLabelId, mergeLoadedLabels } from '../../../../../../common/labels/labelActions';
import { addLabelToElements, ClientAnalytics } from '../../../../../element/labels/elementLabelActions';
import { getNewTransactionId } from '../../../../../utils/undoRedo/undoRedoTransactionManager';

// Types
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { Label, LabelCreationData } from '../../../../../../common/labels/labelTypes';
import { selectAllLabels } from '../../../../../element/labels/labelSelectors';
import { prop } from '../../../../../../common/utils/immutableHelper';
import { uniq } from 'lodash/fp';
import { fetchLabelsByIds } from '../../../../../element/labels/labelsService';
import { userLocalePreferenceSelector } from '../../../../../user/currentUserSelector';
import { getLabelColor } from './labelColorUtils';

export const createNewLabelThunk =
    (labelData: LabelCreationData, transactionId?: number): Thunk<Label> =>
    (dispatch: Function, getState: Function) => {
        const { name, creator, color } = labelData;
        const locale = userLocalePreferenceSelector(getState());

        const label = {
            _id: getNewLabelId(),
            name,
            color: color || getLabelColor(name, locale),
            meta: {
                creator,
                createdTime: Date.now(),
                modifiedTime: Date.now(),
            },
        };

        dispatch(createLabel(label, transactionId));
        return label;
    };

export const createAndAddLabelToElementsThunk =
    (labelData: LabelCreationData, elements: Immutable.List<ImMNElement>, analytics?: ClientAnalytics): Thunk =>
    (dispatch: Function) => {
        const sharedTransactionId = getNewTransactionId();
        const newLabel = dispatch(createNewLabelThunk(labelData, sharedTransactionId));
        dispatch(addLabelToElements(elements, newLabel._id, sharedTransactionId, analytics));
    };

export const fetchAndLoadLabelsByIdsThunk =
    (labelIds: string[]): Thunk =>
    async (dispatch: Function, getState: Function) => {
        const labels = selectAllLabels(getState()) || {};
        const hasLabel = (labelId: string) => prop(labelId, labels);
        const unknownLabelIds = uniq(labelIds.filter((labelId) => labelId && !hasLabel(labelId)));

        if (!unknownLabelIds.length) return;

        const fetchedLabels = await fetchLabelsByIds(unknownLabelIds);
        if (!fetchedLabels.length) return;

        dispatch(mergeLoadedLabels(fetchedLabels));
    };
